import type { FC } from "react";
import React, { useEffect, useState } from "react";
import { SubmissionError } from "redux-form";
import * as Sentry from "@sentry/react";

import { Box, Loader, Typography } from "@braintrust/braintrust-ui-components";
import type { FetchOTPAuthURIResponse } from "@js/apps/auth/actions";
import {
  enableOTPAuth,
  fetchCurrentUser,
  fetchOTPAuthURI,
} from "@js/apps/auth/actions";
import {
  useBeginPasskeyStepUpMutation,
  useCompletePasskeyStepUpMutation,
} from "@js/apps/auth/api";
import { useWebAuthnAssertion } from "@js/apps/common/forms/fields/otp/webauthn-assertion";
import { useHasPasskeyEnabled } from "@js/apps/common/hooks/use-has-passkey-enabled";
import { Modal, ModalConfirm } from "@js/components/modal";
import { Snackbar } from "@js/components/snackbar";
import { createFormInstance } from "@js/forms/components";
import { useAppDispatch } from "@js/hooks";
import { isErrorWithErrors } from "@js/types/errors";

import { EnableOTPContent } from "./content";

import styles from "./styles.module.scss";

const ENABLE_OTP_AUTH_MODAL_ID = "enable-otp-auth-modal";
const ENABLE_OTP_AUTH_FORM_ID = "enable-otp-auth-form";

export const EnableOTPAuthModalInstance = Modal(ENABLE_OTP_AUTH_MODAL_ID, {
  className: styles.modal,
  closeButton: false,
});

type FormData = {
  code: string;
  current_password?: string;
};

const FormInstance = createFormInstance<FormData, unknown>(
  ENABLE_OTP_AUTH_FORM_ID,
);

type EnableOTPAuthFormProps = {
  onSuccessAction?: (codes: string[]) => void;
};
export const EnableOTPAuthForm: FC<EnableOTPAuthFormProps> = ({
  onSuccessAction = () => {},
}) => {
  const dispatch = useAppDispatch();
  const hasPasskey = useHasPasskeyEnabled();
  const [passkeyStepUpKey, setPasskeyStepUpKey] = useState<string>();
  const [OTPAuthURI, setOTPAuthURI] = React.useState<
    FetchOTPAuthURIResponse | ""
  >("");

  const onCancelAction = EnableOTPAuthModalInstance.close;

  useEffect(() => {
    if (!OTPAuthURI) {
      fetchOTPAuthURI()
        .then(setOTPAuthURI)
        .catch(() =>
          Snackbar.error(
            "Inconsistent 2FA status - maybe it was already enabled (please try refreshing the page)?",
            { preventDuplicate: true },
          ),
        );
    }
  }, [OTPAuthURI]);

  const [beginPasskeyStepUp] = useBeginPasskeyStepUpMutation();
  const [completePasskeyStepUp] = useCompletePasskeyStepUpMutation();
  const {
    authenticate,
    isAuthenticating,
    error: passkeyError,
  } = useWebAuthnAssertion({
    begin: () => beginPasskeyStepUp().unwrap(),
    complete: (assertion) => completePasskeyStepUp(assertion).unwrap(),
  });

  const handlePasskeyAuthenticate = async () => {
    const result = await authenticate();
    if (result) {
      setPasskeyStepUpKey(result.key);
    }
  };

  const onSubmit = async (values: FormData) => {
    try {
      const data = await enableOTPAuth({
        code: values.code,
        current_password: hasPasskey ? undefined : values.current_password,
        passkey_step_up_key: hasPasskey ? passkeyStepUpKey : undefined,
      });

      onSuccessAction(data.backup_codes);
      EnableOTPAuthModalInstance.close();
      await dispatch(fetchCurrentUser());
      Snackbar.success("2-Step Authentication enabled.");
      setOTPAuthURI("");
    } catch (error: unknown) {
      if (isErrorWithErrors(error)) {
        throw new SubmissionError(error.errors);
      }

      Snackbar.error("Failed to enable 2FA");
      Sentry.captureException("Enable OTP exception", {
        extra: { error },
      });
      throw new SubmissionError({});
    }
  };

  return (
    <FormInstance onSubmit={onSubmit}>
      <Typography component="h3" variant="title" size="small">
        Set up 2-step authentication
      </Typography>
      {!!OTPAuthURI ? (
        <ModalConfirm
          onCancel={onCancelAction}
          onConfirm="submit"
          confirmText="Enable"
          confirmDisabled={hasPasskey && !passkeyStepUpKey}
          buttonsSquareShape
          buttonsOnEnds
          cancelButtonVariant="secondary"
          confirmButtonVariant="positive-2"
        >
          <EnableOTPContent
            OTPAuthURI={OTPAuthURI.otp_auth_uri}
            OTPAuthSecret={OTPAuthURI.otp_secret}
            renderPasswordField={!hasPasskey}
            passkeyConfirmation={
              hasPasskey
                ? {
                    isConfirmed: !!passkeyStepUpKey,
                    isAuthenticating,
                    error: passkeyError,
                    onAuthenticate: handlePasskeyAuthenticate,
                  }
                : undefined
            }
          />
        </ModalConfirm>
      ) : (
        <Box style={{ minHeight: "200px", position: "relative" }}>
          <Loader centered />
        </Box>
      )}
    </FormInstance>
  );
};

export const openEnableOTPAuthModal = (config?: EnableOTPAuthFormProps) => {
  EnableOTPAuthModalInstance.open({
    children: <EnableOTPAuthForm {...config} />,
  });
};
